import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Building2, Plus, Trash2, Loader2, Save, MapPin, Hash, Star } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const inputCls = "bg-slate-950 border-slate-800 focus:border-[#0A8CF2] text-slate-100 h-9";
const newId = () => `s_${Math.random().toString(36).slice(2, 8)}`;

// Rooftops sharing one inventory: which store's CRM gets a lead is decided per vehicle by the website's
// "Vehicle Location" (when mapped) → stock-number prefix → group default inbox.
export function StoresRoutingCard({ dealer, isAdmin, onSaved }) {
  const [stores, setStores] = useState([]);
  const [defaultId, setDefaultId] = useState(null);
  const [rules, setRules] = useState([]);
  const [locs, setLocs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [counts, setCounts] = useState(null);

  useEffect(() => {
    if (!dealer) return;
    setStores(dealer.stores || []);
    setDefaultId(dealer.default_store_id || null);
    setRules(dealer.store_stock_rules || []);
    setLocs(dealer.store_location_map || []);
  }, [dealer]);

  const storeName = (id) => stores.find((s) => s.id === id)?.name || "—";
  const updStore = (i, patch) => setStores((l) => l.map((s, k) => (k === i ? { ...s, ...patch } : s)));
  const StorePicker = ({ value, onChange, testId }) => (
    <Select value={value || ""} onValueChange={onChange}>
      <SelectTrigger data-testid={testId} className={`${inputCls} w-44`}><SelectValue placeholder="Store" /></SelectTrigger>
      <SelectContent className="bg-[#12141C] border-[#2A2E3D] text-slate-200">
        {stores.map((s) => <SelectItem key={s.id} value={s.id}>{s.name || "Unnamed store"}</SelectItem>)}
      </SelectContent>
    </Select>
  );

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/dealer/stores", { stores, default_store_id: defaultId, store_stock_rules: rules, store_location_map: locs });
      setCounts(data.counts);
      onSaved?.(data.dealer);
      const un = data.counts?.unassigned || 0;
      toast.success(stores.length ? `Routing saved · ${un ? `${un} unit${un === 1 ? "" : "s"} unassigned` : "every unit has a store"}` : "Stores removed — all leads go to the CRM inbox above");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="rounded-xl border border-[#2A2E3D] bg-[#12141C] p-6" data-testid="stores-section">
      <div className="flex items-center gap-2 mb-1">
        <Building2 className="h-4 w-4 text-[#0A8CF2]" />
        <h2 className="font-heading font-bold">Stores &amp; lead routing</h2>
        {stores.length > 0 && <span className="ml-auto text-xs text-slate-500">{stores.length} store{stores.length === 1 ? "" : "s"}</span>}
      </div>
      <p className="text-xs text-slate-400 mb-4">
        For dealer groups that pool inventory across rooftops. Each unit is assigned a store — by the website's <b className="text-slate-300">Vehicle Location</b> when you've mapped it, otherwise by <b className="text-slate-300">stock-number prefix</b> (longest match wins) — and its leads go to that store's inbox. Stores without an inbox, and units that match nothing, use the group default inbox and are flagged <span className="text-amber-300">Unassigned</span>.
      </p>

      <div className="space-y-2" data-testid="stores-list">
        {stores.map((s, i) => (
          <div key={s.id} className="flex flex-col sm:flex-row gap-2 sm:items-center" data-testid={`store-row-${i}`}>
            <button type="button" onClick={() => setDefaultId(s.id)} disabled={!isAdmin} data-testid={`store-default-${i}`} title="Group default inbox — unassigned units and stores without an inbox route here"
              className={`h-9 w-9 shrink-0 rounded-md border flex items-center justify-center ${defaultId === s.id ? "border-amber-400 text-amber-300 bg-amber-950/40" : "border-slate-800 text-slate-600 hover:text-slate-300"}`}>
              <Star className="h-4 w-4" fill={defaultId === s.id ? "currentColor" : "none"} />
            </button>
            <Input data-testid={`store-name-${i}`} value={s.name} disabled={!isAdmin} onChange={(e) => updStore(i, { name: e.target.value })} placeholder="Store name (e.g. Abeloff Kia · GMC)" className={`${inputCls} sm:w-56`} />
            <Input data-testid={`store-email-${i}`} value={s.crm_email || ""} disabled={!isAdmin} onChange={(e) => updStore(i, { crm_email: e.target.value })} placeholder="CRM inbox (blank = group default)" className={`${inputCls} flex-1`} />
            {isAdmin && (
              <button type="button" onClick={() => setStores((l) => l.filter((_, k) => k !== i))} data-testid={`store-remove-${i}`} title="Remove store" className="h-9 w-9 rounded-md border border-slate-800 text-slate-500 hover:text-red-400 hover:border-red-900 flex items-center justify-center">
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        ))}
        {isAdmin && (
          <button type="button" onClick={() => setStores((l) => [...l, { id: newId(), name: "", crm_email: "" }])} data-testid="store-add" className="inline-flex items-center gap-1.5 text-xs text-[#7FC3FF] hover:text-white">
            <Plus className="h-3.5 w-3.5" /> Add store
          </button>
        )}
      </div>

      {stores.length > 0 && (
        <div className="grid md:grid-cols-2 gap-5 mt-5">
          <div>
            <div className="flex items-center gap-1.5 text-sm font-medium mb-2"><Hash className="h-3.5 w-3.5 text-slate-400" /> Stock-number prefixes</div>
            <div className="space-y-2" data-testid="stock-rules">
              {rules.map((r, i) => (
                <div key={i} className="flex gap-2 items-center" data-testid={`stock-rule-${i}`}>
                  <Input data-testid={`stock-rule-prefix-${i}`} value={r.prefix} disabled={!isAdmin} onChange={(e) => setRules((l) => l.map((x, k) => (k === i ? { ...x, prefix: e.target.value.toUpperCase() } : x)))} placeholder="NT" className={`${inputCls} w-24 font-mono-tech uppercase`} />
                  <span className="text-slate-500 text-xs">→</span>
                  <StorePicker value={r.store_id} testId={`stock-rule-store-${i}`} onChange={(v) => setRules((l) => l.map((x, k) => (k === i ? { ...x, store_id: v } : x)))} />
                  {isAdmin && <button type="button" onClick={() => setRules((l) => l.filter((_, k) => k !== i))} data-testid={`stock-rule-remove-${i}`} className="text-slate-500 hover:text-red-400"><Trash2 className="h-3.5 w-3.5" /></button>}
                </div>
              ))}
              {isAdmin && <button type="button" onClick={() => setRules((l) => [...l, { prefix: "", store_id: stores[0]?.id }])} data-testid="stock-rule-add" className="inline-flex items-center gap-1.5 text-xs text-[#7FC3FF] hover:text-white"><Plus className="h-3.5 w-3.5" /> Add prefix</button>}
            </div>
            <p className="text-[11px] text-slate-500 mt-2">Stock "NT26248" matches NT before N. Used-car purchase prefixes (e.g. ZN, ZK) work the same way.</p>
          </div>
          <div>
            <div className="flex items-center gap-1.5 text-sm font-medium mb-2"><MapPin className="h-3.5 w-3.5 text-slate-400" /> Website "Vehicle Location"</div>
            <div className="space-y-2" data-testid="location-map">
              {locs.map((m, i) => (
                <div key={i} className="flex gap-2 items-center" data-testid={`location-rule-${i}`}>
                  <Input data-testid={`location-rule-name-${i}`} value={m.location} disabled={!isAdmin} onChange={(e) => setLocs((l) => l.map((x, k) => (k === i ? { ...x, location: e.target.value } : x)))} placeholder="Abeloff Kia" className={`${inputCls} flex-1`} />
                  <span className="text-slate-500 text-xs">→</span>
                  <StorePicker value={m.store_id} testId={`location-rule-store-${i}`} onChange={(v) => setLocs((l) => l.map((x, k) => (k === i ? { ...x, store_id: v } : x)))} />
                  {isAdmin && <button type="button" onClick={() => setLocs((l) => l.filter((_, k) => k !== i))} data-testid={`location-rule-remove-${i}`} className="text-slate-500 hover:text-red-400"><Trash2 className="h-3.5 w-3.5" /></button>}
                </div>
              ))}
              {isAdmin && <button type="button" onClick={() => setLocs((l) => [...l, { location: "", store_id: stores[0]?.id }])} data-testid="location-rule-add" className="inline-flex items-center gap-1.5 text-xs text-[#7FC3FF] hover:text-white"><Plus className="h-3.5 w-3.5" /> Add location</button>}
            </div>
            <p className="text-[11px] text-slate-500 mt-2">Exact text the website shows as the unit's location. A location beats the stock prefix — leave a site's catch-all location (one it shows on every car) unmapped so the prefix decides.</p>
          </div>
        </div>
      )}

      {isAdmin && (
        <div className="flex flex-wrap items-center gap-3 mt-5">
          <Button onClick={save} disabled={busy} data-testid="stores-save-btn" className="bg-[#0A8CF2] hover:bg-[#3AA3FF] font-bold">
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />} Save routing &amp; re-run on inventory
          </Button>
          {counts && (
            <span className="text-xs text-slate-400" data-testid="stores-counts">
              {Object.entries(counts).map(([k, n]) => `${k === "unassigned" ? "Unassigned" : storeName(k)}: ${n}`).join(" · ")}
            </span>
          )}
        </div>
      )}
    </section>
  );
}
